import type { ResumeDto, UpdateResumeDto } from "@reactive-resume/dto";
import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import debounce from "lodash.debounce";

import { RESUME_PREVIEW_KEY } from "@/client/constants/query-keys";
import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

const persistResume = async (data: UpdateResumeDto) => {
  const response = await axios.patch<ResumeDto, AxiosResponse<ResumeDto>, UpdateResumeDto>(
    `/resume/${data.id}`,
    data,
  );

  queryClient.setQueryData<ResumeDto>(["resume", { id: response.data.id }], response.data);

  queryClient.setQueryData<ResumeDto[]>(["resumes"], (cache) => {
    if (!cache) return [response.data];
    return cache.map((resume) => {
      if (resume.id === response.data.id) return response.data;
      return resume;
    });
  });

  void queryClient.invalidateQueries({
    queryKey: [RESUME_PREVIEW_KEY, { id: response.data.id }],
  });

  return response.data;
};

const pendingSaves = new Map<string, Promise<ResumeDto>>();
export const updateResume = (data: UpdateResumeDto) => {
  const id = data.id ?? "";
  const previous = pendingSaves.get(id) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(() => persistResume(data));
  pendingSaves.set(id, next);
  void next
    .finally(() => {
      if (pendingSaves.get(id) === next) pendingSaves.delete(id);
    })
    .catch(() => undefined);
  return next;
};

export const debouncedUpdateResume = debounce(updateResume, 300);

export const useUpdateResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: updateResumeFn,
  } = useMutation({
    mutationFn: updateResume,
  });

  return { updateResume: updateResumeFn, loading, error };
};
